import { Container, Graphics, Sprite, type Texture } from "pixi.js";
import type { PresentationTaskRunner, TweenEasing } from "@slot/presentation";
import { BASE_ROWS, GRID_CELL_COUNT, columnOf, rowOf } from "../gridSize";
import { GridFrameView } from "./GridFrameView";
import { CELL, GRID_AREA_OFFSET_Y, columnCentreX, frameBox, panelClip, rowCentreY } from "./layout";
import { RowFrameView } from "./RowFrameView";

/**
 * The grid as a container, its origin at the base grid's centre. Bottom to top: every cell's blue background (the
 * window its strip rolls in; a blank is a symbol, the empty cell's plaque), the red that replaces it under an active
 * line's cells (decision 56) and under the coins that start the bonus, the gold frame (sliced, so it grows from the base
 * rows to all five), the clip the symbols show through (the grid layer's root and the multiplier badges: a landed coin
 * overflows its cell over the bars), and the overlay above it all (the trigger's bursts). The row frame around the
 * active lines' rows (the base game's only) lies in the grid layer's root, over every rolling window and under every
 * landed symbol, as in the mock-up: a strip rolls behind its bars, a coin lands over them. The GROW rows' backgrounds
 * are hidden until the frame grows over them. Nothing here is interactive: the taps are the shell's surface over the
 * scene.
 */
/** How far under every symbol the rolling windows stack (more than the grid's height). */
const ROLLING_BELOW = 100_000;
/** The row frame: between the rolling windows and the landed symbols. */
const ROW_FRAME_DEPTH = -ROLLING_BELOW / 2;

export class GridContainer extends Container {
  /** The grid layer's layout root: its area's centre sits one row below the origin (row 1 at the origin). */
  public readonly gridArea: Container;
  /** The cells' parent, inside the clip. */
  public readonly gridRoot: Container;
  /** Over the symbols, inside the clip, in the grid root's space: the cell multiplier badges. */
  public readonly badgeLayer: Container;
  /** Over the clip, in this container's space. */
  public readonly overlay: Container;
  /** Over the gold frame, under the coins: what burns on the frame (GROW's edge fire). */
  public readonly frameOverlay: Container;
  /** Over the cells and the frame, under the coins: the cells that hold a multiplier burning. */
  public readonly cellFireLayer: Container;
  private readonly frame: GridFrameView | null;
  private readonly rowFrame: RowFrameView | null;
  private readonly backgrounds: Sprite[] = [];
  private readonly lit: Sprite[] = [];
  private readonly glows: Sprite[] = [];
  private readonly clip: Graphics;
  private rowsInPlay: number = BASE_ROWS;

  public constructor(input: { frame: Texture | null; rowFrame: Texture | null; cellBlue: Texture | null; cellRed: Texture | null; runner: PresentationTaskRunner }) {
    super({ label: "GridContainer" });

    const backgrounds = new Container({ label: "CellBackgrounds" });
    const lit = new Container({ label: "CellLit" });
    const glows = new Container({ label: "CellGlows" });
    const cellSprite = (texture: Texture, label: string, layer: Container, list: Sprite[], index: number): void => {
      const sprite = new Sprite({ texture, anchor: 0.5, label: `${label}${index}` });
      sprite.setSize(CELL.background.width, CELL.background.height);
      sprite.position.set(columnCentreX(columnOf(index)), rowCentreY(rowOf(index)));
      layer.addChild(sprite);
      list.push(sprite);
    };
    for (let index = 0; index < GRID_CELL_COUNT; index++) {
      if (input.cellBlue) cellSprite(input.cellBlue, "CellBackground", backgrounds, this.backgrounds, index);
      if (input.cellRed) {
        cellSprite(input.cellRed, "CellLit", lit, this.lit, index);
        cellSprite(input.cellRed, "CellGlow", glows, this.glows, index);
      }
    }
    for (const sprite of [...this.lit, ...this.glows]) sprite.alpha = 0;
    this.addChild(backgrounds, lit, glows);

    this.frame = input.frame ? new GridFrameView(input.frame) : null;
    if (this.frame) this.addChild(this.frame);
    // The cells that hold a multiplier burn here: a little bigger than the cell, over the frame's bars, under the coins.
    this.cellFireLayer = new Container({ label: "CellFireLayer" });
    this.addChild(this.cellFireLayer);
    this.frameOverlay = new Container({ label: "FrameOverlay" });
    this.addChild(this.frameOverlay);

    const panel = new Container({ label: "GridPanel" });
    this.clip = new Graphics();
    this.clip.label = "PanelClip";
    panel.addChild(this.clip);
    panel.mask = this.clip;
    this.gridArea = new Container({ label: "GridArea" });
    this.gridArea.position.set(0, GRID_AREA_OFFSET_Y);
    this.gridRoot = new Container({ label: "GridRoot" });
    // The row frame around the active lines' rows (the mock-up's middle row at one line): in the root's space, the grid
    // area's offset taken off (the grid layer places cells in the root and never moves it).
    this.rowFrame = input.rowFrame ? new RowFrameView(input.rowFrame, GRID_AREA_OFFSET_Y, input.runner) : null;
    if (this.rowFrame) {
      this.rowFrame.zIndex = ROW_FRAME_DEPTH;
      this.gridRoot.addChild(this.rowFrame);
    }
    // A lower row's coin overlaps the one above it, as in the mock-up, whichever landed last: the symbols stack by their
    // row. The rolling windows (the masked children) stay under every symbol, so a landed coin, or one leaving its window
    // to land, lies over the cells below it that still roll; the row frame lies between the two.
    this.gridRoot.sortableChildren = true;
    this.gridRoot.onRender = () => {
      for (const child of this.gridRoot.children) {
        if (child !== this.rowFrame) child.zIndex = child.mask ? child.y - ROLLING_BELOW : child.y;
      }
    };
    this.gridArea.addChild(this.gridRoot);
    this.badgeLayer = new Container({ label: "CellMultipliers" });
    this.badgeLayer.position.set(0, GRID_AREA_OFFSET_Y);
    panel.addChild(this.gridArea, this.badgeLayer);

    this.overlay = new Container({ label: "GridOverlay" });
    this.addChild(panel, this.overlay);
    this.layoutRows(BASE_ROWS);
  }

  /** The gold frame (the shine glints across it). */
  public get frameNode(): Container | null {
    return this.frame;
  }

  /** The row frame around the active lines' rows, while it shows (the shine glints across it). */
  public get rowFrameNode(): Container | null {
    return this.rowFrame?.visible ? this.rowFrame : null;
  }

  /** The rows in play now (3, or 5 while GROW is active). */
  public get rows(): number {
    return this.rowsInPlay;
  }

  /**
   * Lays the frame, the clip and the backgrounds out for `rows` rows in play (fractional while it grows): the GROW rows'
   * backgrounds fade in with the frame passing over them.
   */
  public layoutRows(rows: number): void {
    this.rowsInPlay = rows;
    this.frame?.layoutRows(rows);
    const clip = panelClip(rows);
    this.clip.clear().rect(clip.x, clip.y, clip.width, clip.height).fill(0xffffff);
    for (let index = 0; index < this.backgrounds.length; index++) {
      const row = rowOf(index);
      const shown = row < BASE_ROWS ? 1 : Math.max(0, Math.min(1, rows - row));
      this.backgrounds[index]!.alpha = shown;
      this.backgrounds[index]!.visible = shown > 0;
    }
  }

  /** The frame's bottom edge for `rows` rows in play (the meter hangs from it). */
  public frameBottom(rows: number = this.rowsInPlay): number {
    const box = frameBox(rows);
    return box.y + box.height;
  }

  /** A cell's centre in this container's space. */
  public cellCentre(index: number): { x: number; y: number } {
    return { x: columnCentreX(columnOf(index)), y: rowCentreY(rowOf(index)) };
  }

  /**
   * The row frame around `rows` (the active lines', decision 56), or none (the bonus plays no line). A frame in view
   * moves to new rows with `motion`; one coming back into view is there at once.
   */
  public setRowFrame(rows: readonly number[] | null, motion?: { seconds: number; easing: TweenEasing }): void {
    if (!this.rowFrame) return;
    const shown = rows !== null && rows.length > 0;
    if (shown) this.rowFrame.span(rows, this.rowFrame.visible ? motion : undefined);
    this.rowFrame.visible = shown;
  }

  /** The red under `indices` (decision 56): the active lines' cells, rolling or still; the others blue. */
  public setLit(indices: ReadonlySet<number>): void {
    this.lit.forEach((red, index) => {
      red.alpha = indices.has(index) ? 1 : 0;
    });
  }

  /** The red under `indices` at `alpha` (the coins that start the bonus); the others blue. */
  public setGlowing(indices: ReadonlySet<number>, alpha: number): void {
    this.glows.forEach((glow, index) => {
      glow.alpha = indices.has(index) ? alpha : 0;
    });
  }
}
